import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationServices from '../../services/NotificationServices';
import { resolveCurrentUserId } from '../../utils/authSessionStorage';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {Drawer,Typography,Box,Divider,IconButton,Avatar,Badge,Tabs,Tab,CircularProgress,} from '@mui/material';

const InAppNotifications = ({ open, onClose }) => {
  const [tab, setTab] = useState('new');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const scrollBoxRef = useRef(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setTab('new');
      setExpandedIds(new Set());
      setSelectedNotificationId(null);
      setOffset(0);
      setNotifications([]);
      setHasMore(true);
      setTotalItems(0);
      loadingRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    setExpandedIds(new Set());
    setSelectedNotificationId(null);
    setOffset(0);
    setNotifications([]);
    setHasMore(true);
    setTotalItems(0);
    loadingRef.current = false;
  }, [tab]);

  // Format relative time
  const getRelativeTime = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInSeconds = Math.floor((now - created) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} day ago`;
    return created.toLocaleDateString();
  };

  const toggleExpanded = async (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      try {
        await NotificationServices.markAsRead(id);
        setNotifications(prev => prev.map(n => 
          n.id === id ? { ...n, is_read: true } : n
        ));
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
    setExpandedIds(newExpanded);
  };

  const getTruncatedMessage = (message, isExpanded) => {
    if (isExpanded) return message;
    const lines = message.split('\n');
    if (lines.length > 2) {
      return lines.slice(0, 2).join('\n') + '...';
    }
    if (message.length > 85) {
      return message.substring(0, 73) + '...';
    }
    return message;
  };

  useEffect(() => {
    if (!open) return;

    const fetchNotifications = async () => {
      try {
        if (offset === 0) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);
        
        const currentUserId = resolveCurrentUserId();
        if (!currentUserId) {
          setLoading(false);
          setIsLoadingMore(false);
          return;
        }
        const limitToRequest = offset === 0 
          ? 10 
          : Math.min(10, totalItems - offset);
        
        const params = {
          limit: limitToRequest,
          offset: offset,
          user_id: currentUserId,
          type: 'in_app',
        };

        if (tab === 'new') {
          params.is_read = false;
        } else if (tab === 'read') {
          params.is_read = true;
        }

        const response = await NotificationServices.get(params);
        console.log('Fetched notifications:', response);
        
        let notificationsArray = [];
        let totalItemsFromAPI = 0;
        
        if (response && response.data && response.data.notifications && Array.isArray(response.data.notifications.data)) {
          notificationsArray = response?.data?.notifications?.data;
          totalItemsFromAPI = response?.data?.notifications?.totalItems || 0;
        }
        if (totalItemsFromAPI > 0) {
          setTotalItems(totalItemsFromAPI);
        }
        if (offset === 0) {
          setNotifications(notificationsArray);
        } else {
          setNotifications(prev => [...prev, ...notificationsArray]);
        }
        const currentLoadedCount = offset === 0 ? notificationsArray.length : notifications.length + notificationsArray.length;
        setHasMore(currentLoadedCount < totalItemsFromAPI);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications');
        if (offset === 0) {
          setNotifications([]);
        }
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        loadingRef.current = false;
      }
    };

    fetchNotifications();
  }, [open, tab, offset]);


  const filteredNotifications = useMemo(() => {
    return notifications;
  }, [notifications]);

  const handleScroll = (event) => {
    const scrollElement = event.target;
    if (
      scrollElement.scrollHeight - scrollElement.scrollTop <= scrollElement.clientHeight + 100
      && hasMore
      && !loadingRef.current
      && notifications.length > 0
    ) {
      loadingRef.current = true;
      setOffset(prev => prev + 10);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

          <Typography variant="h6" fontWeight={600}>
            Notifications
          </Typography>
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        sx={{
          px: 1,
          bgcolor: 'background.default',
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: 1.5,
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 48,
          },
        }}
      >
        <Tab label={`New `} value="new" />
        <Tab label={`Read `} value="read" />
        <Tab label={`All `} value="all" />
      </Tabs>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }} ref={scrollBoxRef} onScroll={handleScroll}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          filteredNotifications.map((n) => {
            const isExpanded = expandedIds.has(n.id);
            const isSelected = selectedNotificationId === n.id;
            return (
            <Box
              key={n.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                p: 1.5,
                mb: 1,
                borderRadius: 2,
                bgcolor: (theme) => n.is_read 
                  ? (theme.palette.mode === 'dark' ? 'grey.900' : 'action.hover')
                  : 'primary.soft',
                cursor: 'pointer',
                transition: 'all 0.2s',
                alignItems: 'flex-start',
                flexDirection: 'column',
                '&:hover': {
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'action.selected',
                },
              }}
              onClick={async () => {
                if (!isSelected) {
                  try {
                    await NotificationServices.markAsRead(n.id);
                    setNotifications(prev => prev.map(notif => 
                      notif.id === n.id ? { ...notif, is_read: true } : notif
                    ));
                  } catch (err) {
                    console.error('Error marking notification as read:', err);
                  }
                }
                setSelectedNotificationId(isSelected ? null : n.id);
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, width: '100%', alignItems: 'flex-start' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={n.is_read}
                    overlap="circular"
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'grey.200',
                        color: 'primary.main',
                        width: 36,
                        height: 36,
                        border: 1,
                        borderColor: 'primary.light',
                      }}
                    >
                      {n.is_read ? (
                        <NotificationsIcon fontSize="small" />
                      ) : (
                        <NotificationsActiveIcon fontSize="small" />
                      )}
                    </Avatar>
                  </Badge>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={n.is_read ? 500 : 700}
                    >
                      {n.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getRelativeTime(n.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

             
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: -2, ml: 6, width: 'calc(100% - 44px)', transition: 'opacity 0.2s' }}
              >
                {isSelected ? n.message : getTruncatedMessage(n.message, isExpanded)}
              </Typography>

             
              {n.message && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    width: '100%',
                    justifyContent: 'flex-end',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelected) {
                      setSelectedNotificationId(null);
                    } else {
                      toggleExpanded(n.id);
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {isExpanded || isSelected ? (
                      <>
                        Show less <ExpandLessIcon sx={{ fontSize: 14 }} />
                      </>
                    ) : (
                      <>
                        Show more <ExpandMoreIcon sx={{ fontSize: 14 }} />
                      </>
                    )}
                  </Typography>
                </Box>
              )}

              {(isExpanded || isSelected) && n.link && (
                <>
                  <Divider sx={{ width: '100%' }} />
                  <Box
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    onClick={() => window.open(n.link, '_blank')}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      fontSize={14}
                      sx={{
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View case details →
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            );
          })
        )}
        
        {isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <CircularProgress size={30} />
          </Box>
        )}
        
        {!hasMore && notifications.length > 0 && !isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              No more notifications
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      <Box
        sx={{
          p: 1.5,
          textAlign: 'center',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="body2"
          color="primary"
          sx={{
            cursor: 'pointer',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.8,
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={async () => {
            try {
              await NotificationServices.markAllAsRead();
              setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            } catch (err) {
              console.error('Error marking all notifications as read:', err);
            }
          }}
        >
          <DoneAllIcon fontSize="small" />
          Mark as read all
        </Typography>
      </Box>
    </Drawer>
  );
};

export default InAppNotifications;